<template>
  <RouterLink v-if="to" :to="to" :class="['c-button', styles]">
    <ButtonLoading v-if="loading" />
    <div class="c-button__body">
      <slot name="icon" />
      <div class="c-button__content"><slot /></div>
    </div>
  </RouterLink>
  <a
    v-else-if="href"
    :href="href"
    :target="target"
    :class="['c-button', styles]"
    @click="$emit('click', $event)"
  >
    <ButtonLoading v-if="loading" />
    <div class="c-button__body">
      <slot name="icon" />
      <div class="c-button__content"><slot /></div>
    </div>
  </a>
  <button v-else :class="['c-button', styles]" @click="$emit('click', $event)">
    <ButtonLoading v-if="loading" />
    <span class="c-button__body">
      <slot name="icon" />
      <span class="c-button__content"><slot /></span>
    </span>
  </button>
</template>

<script>
import ButtonLoading from "./ButtonLoading";
const SIZES = {
  TINY: "c-button--tiny",
  SMALL: "c-button--small",
  MEDIUM: "c-button--medium",
  LARGE: "c-button--large",
};

export default {
  name: "AppButton",
  components: { ButtonLoading },
  props: {
    to: {
      type: [String, Object],
      default: "",
    },
    href: {
      type: String,
      default: "",
    },
    outline: {
      type: Boolean,
      default: false,
    },
    bold: {
      type: Boolean,
      default: false,
    },
    round: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    reverseSecondary: {
      type: Boolean,
      default: false,
    },
    negative: {
      type: Boolean,
      default: false,
    },
    shadow: {
      type: Boolean,
      default: false,
    },
    flush: {
      type: Boolean,
      default: false,
    },
    darken: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "MEDIUM",
      validator: (size) => Boolean(SIZES[size]),
    },
    active: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    relative: {
      type: Boolean,
      default: false,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
    center: {
      type: Boolean,
      default: false,
    },
    flex: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String,
      default: "_self",
    },
  },
  computed: {
    styles() {
      return {
        "c-button--outline": this.outline,
        "c-button--bold": this.bold,
        "c-button--primary": this.primary,
        "c-button--secondary": this.secondary,
        "c-button--reverse-secondary": this.reverseSecondary,
        "c-button--negative": this.negative,
        "c-button--shadow": this.shadow,
        "c-button--round": this.round,
        "c-button--flush": this.flush,
        "c-button--active": this.active,
        "c-button--block": this.block,
        "c-button--relative": this.relative,
        "c-button--reverse": this.reverse,
        "c-button--has-icon": this.$slots.icon,
        "c-button--darken": this.darken,
        "c-button--center": this.center,
        "c-button--flex": this.flex,
        "c-button--loading": this.loading,
        [SIZES[this.size]]: true,
      };
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '../../../../sass/_www/variables.global.styl'

.c-button
  background none
  border none
  display inline-flex
  align-items center
  height 46px
  line-height 46px
  margin 0
  padding 0 16px
  font-size 14px
  font-weight 500
  color #212121
  font-family $base-font-family
  border-radius 8px
  cursor pointer
  min-width 1px
  line-height 1
  max-width 100%
  &__body
  &__content
    white-space nowrap
    text-overflow ellipsis
    overflow hidden
  &__body
    display flex
    align-items center
  &:active
  &:hover
    background-color rgba(72, 72, 72, 0.05)
  &:focus
    outline 0
    box-shadow 0 0 0 4px rgba(238, 189, 101, 0.5)
  &--loading
    position relative
  &--loading &__body
    visibility hidden
  &--darken:active
  &--darken:hover
    background-color rgba(255, 255, 255, 0.15)
  &--round
    border-radius 23px
  &--outline
    background none
    box-shadow inset 0 0 0 1px rgba(72, 72, 72, 0.15)
  &--bold
    font-weight 600
  &--outline:focus
    box-shadow 0 0 0 4px rgba(238, 189, 101, 0.5)

  &--shadow
    box-shadow 0 1px 2px rgba(72, 72, 72, 0.15)
  &--shadow:focus
    box-shadow 0 0 0 4px rgba(238, 189, 101, 0.5)
  &--shadow&--outline
    box-shadow inset 0 0 0 1px rgba(72, 72, 72, 0.15), 0 1px 2px rgba(72, 72, 72, 0.15)
  &--shadow&--outline:focus
    box-shadow 0 0 0 4px rgba(238, 189, 101, 0.5)

  &--primary
    background-color rgb(238, 189, 101)
    color #FFF
  &--primary:hover
    background-color darken(rgb(238, 189, 101), 5)
  &--primary&--outline
    background none
    color rgb(238, 189, 101)
  &--primary&--outline:hover
    color #FFF
    background rgb(238, 189, 101)

  &--reverse-secondary
    background-color $base-color-reverse-secondary
    color #FFF
  &--reverse-secondary:hover
      background-color darken($base-color-reverse-secondary, 5)
  &--reverse-secondary&--outline
    background none
    color $base-color-reverse-secondary
  &--reverse-secondary&--outline:hover
    background-color rgba(72, 72, 72, 0.05)
  //   color #FFF
  //   background $base-color-reverse-secondary

  &--secondary
    background-color #DC6C51
    color #FFF
  &--secondary:hover
    background-color darken(#DC6C51, 5)
  &--secondary&--outline
    color #DC6C51
    background none
  &--secondary&--outline:hover
    color #FFF
    background #DC6C51

  &--negative
    background-color #e8384f
    color #FFF
  &--negative:hover
    background-color darken(#e8384f, 5)
  &--negative&--outline
    color #e8384f
    background none
  &--negative&--outline:hover
    color #FFF
    background #e8384f

  &--active
    background-color rgba(220, 108, 81, 0.15)
    color rgb(220, 108, 81)
    box-shadow none
  &--active:hover
    background-color rgba(220, 108, 81, 0.2)

  &--tiny
    height 30px
    line-height 30px
    padding 0 8px
    font-size 12px
    font-weight 500
  &--small
    height 36px
    min-width 36px
    line-height 36px
    padding 0 16px
    font-size 14px
  &--large
    height 60px
    min-width 60px
    line-height 60px
    padding 0 32px
    font-size 18px
    font-weight 700
    @media screen and (max-width 768px)
      height 54px
      line-height 54px
      padding 0 24px
      font-size 16px

  &--center
    justify-content center

  &--flex &__content
    display flex
    align-items center

  &--flush
    padding 0

  &--has-icon &__content
    margin-left 4px

  &--block
    width 100%
  &--relative
    position relative
  &--reverse
    flex-direction row-reverse

  &--reverse&--has-icon &__content
    margin-right 4px
</style>
